import React from 'react';
import { Link } from 'react-router-dom';
const Law = () => {
  const programDetails = {
    title: 'Faculty of Law',
    degree: 'Bachelor of Laws (LLB Hons)',
    duration: '3-4 Years',
    credits: '360 UK Credits',
    intake: 'January, May, September',
    
    overview: 'Our LLB (Hons) Law program provides a comprehensive understanding of legal principles and practices, preparing students for a successful career in the legal profession. The curriculum is designed to develop critical thinking, analytical skills, and practical legal knowledge, while also preparing students for professional legal qualifications.',
    
    highlights: [
      'Qualifying Law Degree recognized by legal professional bodies',
      'Taught by experienced legal practitioners and academics',
      'Mooting and debating opportunities',
      'Legal clinic experience',
      'Strong focus on employability and professional skills'
    ],
    
    coreModules: [
      'Contract Law',
      'Criminal Law',
      'Constitutional & Administrative Law',
      'Law of Torts',
      'Land Law',
      'Equity & Trusts',
      'EU Law',
      'Legal Skills & Research Methods'
    ],
    
    optionalModules: [
      'Human Rights Law',
      'Commercial Law',
      'Family Law',
      'Employment Law',
      'Intellectual Property Law',
      'International Law',
      'Medical Law & Ethics'
    ],
    
    careerPaths: [
      'Solicitor',
      'Barrister',
      'Legal Executive',
      'Paralegal',
      'Compliance Officer',
      'Legal Consultant',
      'Policy Advisor',
      'Human Rights Advocate'
    ],
    
    entryRequirements: [
      'A-levels: BBB or equivalent',
      'International Baccalaureate: 30 points',
      'BTEC: DDM',
      'English language proficiency (IELTS 6.5 with 6.0 in each component)',
      'Personal statement and reference'
    ],
    
    professionalRecognition: [
      'Recognized by the Solicitors Regulation Authority (SRA)',
      'Accredited by the Bar Standards Board (BSB)',
      'Qualifying Law Degree for progression to the Legal Practice Course (LPC) or Bar Professional Training Course (BPTC)'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <h1 className="text-4xl text-red-600 p-8">TEST: Law Component Loaded</h1>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{programDetails.title}</h1>
          <p className="text-2xl text-blue-100">{programDetails.degree}</p>
          <div className="flex flex-wrap gap-6 text-blue-100 mt-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Duration: {programDetails.duration}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {programDetails.credits}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Intake: {programDetails.intake}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Program Overview */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Program Overview</h2>
              <p className="text-gray-700 mb-6">{programDetails.overview}</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Why Study Law With Us?</h3>
              <ul className="space-y-3 mb-6">
                {programDetails.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">✓</span>
                    <span className="text-gray-700">{highlight}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Program Structure */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Program Structure</h2>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Core Modules</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {programDetails.coreModules.map((module, index) => (
                    <div key={index} className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                      <h4 className="font-medium text-blue-800">{module}</h4>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Optional Modules (Select 4)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {programDetails.optionalModules.map((module, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg border-l-2 border-gray-300">
                      <p className="text-gray-700">{module}</p>
                    </div>
                  ))}
                  {programDetails.optionalModules.map((module, index) => (
                    <div key={`opt-${index}`} className="bg-gray-50 p-3 rounded-lg border-l-2 border-gray-300">
                      <p className="text-gray-700">{module}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Career Paths */}
            <section className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Career Opportunities</h2>
              <p className="text-gray-700 mb-6">
                Our LLB program opens doors to various career paths in the legal profession and beyond. Graduates can pursue:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {programDetails.careerPaths.map((path, index) => (
                  <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-700">{path}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Entry Requirements */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Entry Requirements</h2>
              <ul className="space-y-3 mb-6">
                {programDetails.entryRequirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/admissions/apply-now"
                className="mt-4 inline-block bg-blue-900 text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors w-full text-center"
              >
                Apply Now
              </Link>
            </div>

            {/* Professional Recognition */}
            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Professional Recognition</h3>
              <ul className="space-y-3">
                {programDetails.professionalRecognition.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Related Programs</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/academics/foundation" className="text-blue-700 hover:underline">
                    Foundation in Law
                  </Link>
                </li>
                <li>
                  <Link to="/academics/business-management" className="text-blue-700 hover:underline">
                    Business Law
                  </Link>
                </li>
                <li>
                  <Link to="/academics/llm" className="text-blue-700 hover:underline">
                    LLM (Master of Laws)
                  </Link>
                </li>
                <li className="pt-2 mt-4 border-t border-gray-200">
                  <Link to="/admissions/scholarships" className="text-blue-700 font-medium hover:underline">
                    Scholarships Available →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Begin Your Legal Career With Us</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our distinguished Law program and take the first step towards becoming a legal professional.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/admissions/apply-now"
              className="bg-white text-blue-900 px-8 py-3 rounded-md font-medium hover:bg-blue-100 transition-colors"
            >
              Apply Now
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-blue-800 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Law;
